import React, { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { useNavigate, useParams, useLocation, Outlet, NavLink } from "react-router-dom";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Download, RefreshCcw, X } from "lucide-react";
import { cn } from "@/lib/utils";

const fetcher = (url) => api.get(url).then((r) => r.data);

const CATEGORIES = [
  { slug: "overview", label: "Overview" },
  { slug: "revenue-sales", label: "Revenue & Sales" },
  { slug: "customer", label: "Customer" },
  { slug: "membership-voucher", label: "Membership & Voucher" },
  { slug: "operational", label: "Operational" },
  { slug: "appointments", label: "Appointments" },
  { slug: "cost-profitability", label: "Cost & Profitability" },
  { slug: "benchmarking", label: "Branch Benchmarking" },
];

export const DOC_TYPE_OPTIONS = [
  { code: "SS", label: "Service Sale" },
  { code: "GS", label: "Gift Voucher Sale" },
  { code: "GCS", label: "Membership Card Sale" },
  { code: "GCC", label: "Membership Card Recharge" },
  { code: "PS", label: "Product Sale" },
  { code: "OS", label: "Outstanding Collection" },
];

export const PERIOD_KINDS = [
  { kind: "month", label: "Month" },
  { kind: "quarter", label: "Quarter" },
  { kind: "ytd", label: "YTD" },
];

export const DashboardContext = React.createContext(null);
export const useDashboard = () => React.useContext(DashboardContext);

function buildQS({ month, branchCodes, docTypes }) {
  const qs = new URLSearchParams();
  if (month) qs.set("month", month);
  if (branchCodes?.length) qs.set("branches", branchCodes.join(","));
  if (docTypes?.length) qs.set("doc_types", docTypes.join(","));
  return qs;
}

export default function DashboardShell() {
  const { branchCode } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const [month, setMonth] = useState(null);
  const [period, setPeriod] = useState("month");
  const [branchCodes, setBranchCodes] = useState(branchCode ? [branchCode] : []);
  const [cities, setCities] = useState([]);
  const [ownership, setOwnership] = useState([]);
  const [docTypes, setDocTypes] = useState([]);

  const qs = new URLSearchParams();
  if (month) qs.set("month", month);
  if (period !== "month") qs.set("period", period);
  if (branchCodes?.length) qs.set("branches", branchCodes.join(","));
  if (cities?.length) qs.set("cities", cities.join(","));
  if (ownership?.length) qs.set("ownership", ownership.join(","));
  if (docTypes?.length) qs.set("doc_types", docTypes.join(","));
  const { data, isLoading, mutate } = useSWR(
    `/api/dashboard/summary?${qs.toString()}`,
    fetcher
  );

  useEffect(() => {
    if (data?.month && !month) setMonth(data.month);
  }, [data, month]);

  const currentSlug = useMemo(() => {
    const parts = loc.pathname.split("/").filter(Boolean);
    if (parts[0] === "dashboard" && parts[1] && parts[1] !== "branch") return parts[1];
    return "overview";
  }, [loc.pathname]);

  const periodOptions = data?.period_options?.[period]
    || (data?.months || []).map((m) => ({ value: m, label: m }));

  // Switching Month / Quarter / YTD jumps to the newest period of that kind
  const changePeriod = (kind) => {
    if (kind === period) return;
    const opts = data?.period_options?.[kind] || [];
    setPeriod(kind);
    if (opts.length) setMonth(opts[0].value);
  };

  const ctxValue = useMemo(() => ({
    month, setMonth,
    period, periodInfo: data?.period || null,
    branchCodes, setBranchCodes,
    cities, setCities,
    ownership, setOwnership,
    docTypes, setDocTypes,
    months: data?.months || [],
    branches: data?.branches || [],
    cityOptions: data?.cities || [],
    ownershipOptions: data?.ownership_options || [],
    refresh: mutate,
  }), [month, period, branchCodes, cities, ownership, docTypes, data, mutate]);

  const toggleBranch = (code) => {
    setBranchCodes((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);
  };
  const toggleCity = (name) => {
    setCities((prev) => prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]);
  };
  const branchesForOwnership = (ownList) => {
    if (!ownList.length) return [];
    const set = new Set(ownList.map((o) => o.toLowerCase()));
    return (data?.branches || [])
      .filter((b) => set.has((b.ownership_type || "").toLowerCase()))
      .map((b) => b.branch_code);
  };
  const toggleOwnership = (name) => {
    const next = ownership.includes(name)
      ? ownership.filter((c) => c !== name)
      : [...ownership, name];
    setOwnership(next);
    // Auto-sync the Branches filter to the branches in the selected ownership type(s).
    setBranchCodes(branchesForOwnership(next));
  };
  const clearOwnership = () => {
    setOwnership([]);
    setBranchCodes([]);
  };
  const toggleDocType = (code) => {
    setDocTypes((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);
  };

  if (isLoading && !data) {
    return <div className="p-8 text-stone-500 font-mono text-sm">loading dashboard…</div>;
  }

  if (data?.empty) {
    return (
      <div className="p-8 md:p-12 max-w-4xl">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-stone-500">Dashboard</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-stone-900 mt-2">
          No data yet.
        </h1>
        <p className="mt-4 text-stone-600 max-w-lg">
          Upload your first MIS Input File Pack to see the numbers wake up. Every KPI on this dashboard
          is powered by an editable formula in the Metric Studio — nothing is hardcoded.
        </p>
        <div className="mt-8 flex gap-3">
          <Button className="bg-brand hover:bg-brand-hover text-white" onClick={() => nav("/upload")}
                  data-testid="empty-upload-btn">
            Upload MIS file
          </Button>
        </div>
      </div>
    );
  }

  const outletBranches = data?.branches || [];

  return (
    <DashboardContext.Provider value={ctxValue}>
      <div className="p-6 md:p-8 pb-2">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-stone-500" data-testid="dashboard-eyebrow">
              {branchCodes.length === 0 && cities.length === 0 ? "All outlets" :
                [
                  cities.length ? `${cities.length} cit${cities.length > 1 ? "ies" : "y"}` : null,
                  branchCodes.length ? `${branchCodes.length} branch${branchCodes.length > 1 ? "es" : ""}` : null,
                ].filter(Boolean).join(" · ")}
              {docTypes.length > 0 && ` · ${docTypes.length} doc type${docTypes.length > 1 ? "s" : ""}`}
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-light tracking-tighter text-stone-900 mt-0.5">
              Performance <span className="font-mono font-medium tracking-tight text-brand" data-testid="dashboard-period-label">
                {data?.period?.label || month || "—"}
              </span>
            </h1>
            {data?.period && (
              <div className="text-xs text-stone-500 mt-1" data-testid="dashboard-period-comparison">
                Changes shown {data.period.comparison} ({data.period.prior_label})
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-md border border-stone-300 bg-white overflow-hidden h-9" data-testid="period-kind-toggle">
              {PERIOD_KINDS.map((p) => (
                <button
                  key={p.kind}
                  type="button"
                  onClick={() => changePeriod(p.kind)}
                  data-testid={`period-kind-${p.kind}`}
                  className={cn(
                    "px-3 text-sm font-medium transition-colors border-r border-stone-200 last:border-r-0",
                    period === p.kind ? "bg-brand text-white" : "text-stone-600 hover:bg-stone-50"
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Select value={month || ""} onValueChange={setMonth}>
              <SelectTrigger className="w-[260px] h-9 bg-white border-stone-300" data-testid="dashboard-month-select">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                {periodOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`month-opt-${o.value}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={() => mutate()} data-testid="dashboard-refresh-btn" className="h-9 w-9">
              <RefreshCcw className="w-4 h-4" />
            </Button>
            <Button variant="outline" onClick={() => window.print()} data-testid="dashboard-export-btn" className="h-9">
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
          </div>
        </div>

        {/* Capsule filter row: cities */}
        <div className="flex items-start gap-3 py-2" data-testid="city-capsules-row">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mt-1.5 shrink-0 w-16">
            Cities
          </div>
          <div className="flex flex-wrap gap-1.5 flex-1">
            <Capsule
              active={cities.length === 0}
              onClick={() => setCities([])}
              testId="city-cap-all"
            >
              All
            </Capsule>
            {(data?.cities || []).map((c) => (
              <Capsule
                key={c}
                active={cities.includes(c)}
                onClick={() => toggleCity(c)}
                testId={`city-cap-${c}`}
              >
                {c}
                {cities.includes(c) && <X className="w-3 h-3 ml-1 -mr-0.5" />}
              </Capsule>
            ))}
          </div>
        </div>

        {/* Capsule filter row: ownership type */}
        {(data?.ownership_options || []).length > 0 && (
          <div className="flex items-start gap-3 py-2" data-testid="ownership-capsules-row">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mt-1.5 shrink-0 w-16">
              Ownership
            </div>
            <div className="flex flex-wrap gap-1.5 flex-1">
              <Capsule
                active={ownership.length === 0}
                onClick={clearOwnership}
                testId="ownership-cap-all"
              >
                All
              </Capsule>
              {(data?.ownership_options || []).map((o) => (
                <Capsule
                  key={o}
                  active={ownership.includes(o)}
                  onClick={() => toggleOwnership(o)}
                  testId={`ownership-cap-${o}`}
                >
                  {o}
                  {ownership.includes(o) && <X className="w-3 h-3 ml-1 -mr-0.5" />}
                </Capsule>
              ))}
            </div>
          </div>
        )}

        {/* Capsule filter row: branches */}
        <div className="flex items-start gap-3 py-2" data-testid="branch-capsules-row">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mt-1.5 shrink-0 w-16">
            Branches
          </div>
          <div className="flex flex-wrap gap-1.5 flex-1">
            <Capsule
              active={branchCodes.length === 0}
              onClick={() => setBranchCodes([])}
              testId="branch-cap-all"
            >
              All
            </Capsule>
            {outletBranches.map((b) => (
              <Capsule
                key={b.branch_code}
                active={branchCodes.includes(b.branch_code)}
                onClick={() => toggleBranch(b.branch_code)}
                testId={`branch-cap-${b.branch_code}`}
              >
                {b.branch_name || b.branch_code}
                {branchCodes.includes(b.branch_code) && <X className="w-3 h-3 ml-1 -mr-0.5" />}
              </Capsule>
            ))}
          </div>
        </div>

        {/* Capsule filter row: doc types */}
        <div className="flex items-start gap-3 py-2 border-b border-stone-200" data-testid="doctype-capsules-row">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mt-1.5 shrink-0 w-16">
            Doc Type
          </div>
          <div className="flex flex-wrap gap-1.5 flex-1">
            <Capsule
              active={docTypes.length === 0}
              onClick={() => setDocTypes([])}
              testId="doctype-cap-all"
            >
              All
            </Capsule>
            {DOC_TYPE_OPTIONS.map((dt) => (
              <Capsule
                key={dt.code}
                active={docTypes.includes(dt.code)}
                onClick={() => toggleDocType(dt.code)}
                testId={`doctype-cap-${dt.code}`}
              >
                {dt.label}
                {docTypes.includes(dt.code) && <X className="w-3 h-3 ml-1 -mr-0.5" />}
              </Capsule>
            ))}
          </div>
        </div>

        {/* Sub-nav tabs */}
        <div className="-mx-6 md:-mx-8 px-6 md:px-8 border-b border-stone-200">
          <div className="flex gap-1 overflow-x-auto no-scrollbar" data-testid="dashboard-tabs">
            {CATEGORIES.map((c) => {
              const path = c.slug === "overview" ? "/dashboard" : `/dashboard/${c.slug}`;
              return (
                <NavLink
                  key={c.slug}
                  to={path}
                  end={c.slug === "overview"}
                  data-testid={`tab-${c.slug}`}
                  className={({ isActive }) => cn(
                    "px-3.5 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors font-medium",
                    (isActive || currentSlug === c.slug)
                      ? "border-brand text-brand"
                      : "border-transparent text-stone-500 hover:text-stone-900"
                  )}
                >
                  {c.label}
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>

      <div className="px-6 md:px-8 pb-10">
        <Outlet />
      </div>
    </DashboardContext.Provider>
  );
}

function Capsule({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        "inline-flex items-center h-7 px-2.5 rounded-full text-[11px] font-medium transition-colors border",
        active
          ? "bg-brand text-white border-brand shadow-sm"
          : "bg-white text-stone-700 border-stone-300 hover:border-stone-400 hover:bg-stone-50"
      )}
    >
      {children}
    </button>
  );
}
